import type { Field } from "../Field";
import { initField } from "../Field";
import type { InferOutput } from "valibot";
import { boolean, fallback, object } from "valibot";
import { dataName, label, rememberInput, required } from "../../utils/validation/base";
import { useTranslation } from "../../state/i18n";
import { TextInput } from "../../components/inputs/TextInput";
import { useFormServices } from "../../services/formServices";
import { BOOLEAN_CONDITION_KEYS } from "../../constants";
import { isFilledInFilter } from "../../pages/customer/form/filter";
import { CheckboxPreview } from "../FieldPreviewComponents";
import { Toggle } from "../../components/toggle/Toggle";
import PropertySection from "../PropertySection";
import { keyFromSelector } from "i18next";

const Schema = object({
  label_text: label(),
  data_name: dataName(),
  checkbox_default_value: fallback(boolean(), false),
  remember_input: rememberInput(),
  required: required(),
});
const ValueSchema = boolean();

export type CheckboxFieldProperties = InferOutput<typeof Schema>;
type ValueType = InferOutput<typeof ValueSchema>;

const CheckboxField: Field<CheckboxFieldProperties, CheckboxFieldProperties, ValueType> = {
  type: "checkbox",
  category: "choices",
  widget: "com.moreapps:checkbox:1",
  schema: Schema,
  valueSchema: ValueSchema,
  hasData: true,
  defaultProperties: () => ({
    checkbox_default_value: false,
    remember_input: false,
    required: false,
  }),
  ruleBehaviour: {
    conditions: { keys: BOOLEAN_CONDITION_KEYS, variant: "boolean" },
    actions: { variant: "boolean" },
  },
  filter: {
    type: "boolean",
    definition: [
      {
        operator: "is",
        inputType: "select",
        options: [
          { id: "true", label: keyFromSelector(($) => $.CHECKBOX_FIELD_VALUE_TRUE_LABEL) },
          { id: "false", label: keyFromSelector(($) => $.CHECKBOX_FIELD_VALUE_FALSE_LABEL) },
        ],
      },
      isFilledInFilter,
    ],
  },
  stringValue: (value) => (value ? "✓" : "✗"),
  renderValue: (value) => {
    const { t } = useTranslation();
    return value ? t(($) => $.CHECKBOX_FIELD_VALUE_TRUE_LABEL) : t(($) => $.CHECKBOX_FIELD_VALUE_FALSE_LABEL);
  },
  icon: "checkbox",
  titleKey: keyFromSelector(($) => $.CHECKBOX_FIELD_LABEL),
  descriptionKey: keyFromSelector(($) => $.CHECKBOX_FIELD_DESCRIPTION),
  propertyKeys: {
    label_text: keyFromSelector(($) => $.FIELD_PROPERTY_LABEL_TEXT),
    checkbox_default_value: keyFromSelector(($) => $.FIELD_PROPERTY_CHECKBOX_DEFAULT_VALUE),
    data_name: keyFromSelector(($) => $.FIELD_PROPERTY_DATA_NAME),
    required: keyFromSelector(($) => $.FIELD_PROPERTY_REQUIRED),
    remember_input: keyFromSelector(($) => $.FIELD_PROPERTY_REMEMBER_INPUT),
  },
  PreviewContent: (props) => (
    <CheckboxPreview
      label={props.properties.label_text}
      required={props.properties.required}
      value={props.properties.checkbox_default_value}
    />
  ),
  EditItem: (props) => {
    const { t } = useTranslation();
    const { fieldService } = useFormServices();

    return (
      <>
        <PropertySection>
          <TextInput
            label={t(props.propertyKeys.label_text)}
            value={props.data.properties.label_text}
            onBlur={(e) => fieldService.setLabelProperty(props.uid, e.target.value)}
            errorMessage={props.getError("label_text")}
            required
          />
          <Toggle
            block
            label={t(props.propertyKeys.checkbox_default_value)}
            checked={props.data.properties.checkbox_default_value}
            setChecked={(value) => props.setValue("checkbox_default_value", value)}
            description={t(($) => $.FIELD_PROPERTY_CHECKBOX_DEFAULT_VALUE_DESCRIPTION)}
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_VALIDATION)}>
          <Toggle
            block
            label={t(($) => $.REQUIRED_SWITCH)}
            checked={props.data.properties.required}
            setChecked={(checked) => props.setValue("required", checked)}
            description={t(($) => $.FIELD_PROPERTY_REQUIRED_DESCRIPTION)}
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_BEHAVIOUR)}>
          <Toggle
            block
            label={t(($) => $.REMEMBER_INPUT_SWITCH)}
            checked={props.data.properties.remember_input}
            setChecked={(checked) => props.setValue("remember_input", checked)}
            description={t(($) => $.FIELD_PROPERTY_REMEMBER_INPUT_DESCRIPTION)}
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_ADVANCED)}>
          <TextInput
            label={t(props.propertyKeys.data_name)}
            value={props.data.properties.data_name}
            onBlur={(e) => props.setValue("data_name", e.target.value)}
            required
            errorMessage={props.getError("data_name")}
            info={{
              title: t(props.propertyKeys.data_name),
              alt: t(($) => $.FIELD_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.data_name }),
              description: t(($) => $.FIELD_PROPERTY_DATA_NAME_DESCRIPTION),
            }}
          />
        </PropertySection>
      </>
    );
  },
};

export default initField(CheckboxField);
